import React from 'react';
import PropTypes from 'prop-types';
import Tippy from '@tippyjs/react';
import { KIND_ADMISSION_TAX, canMarkAsAdmissionTax } from '../utils/admissionTax';
import {
  TAX_WITHHOLDING_EXEMPT_COLOR,
  TAX_WITHHOLDING_EXEMPTION_ICON,
  TAX_WITHHOLDING_EXEMPTION_LABEL,
  TAX_WITHHOLDING_EXEMPTION_TEST_ID,
  canToggleTaxWithholdingExemption,
  taxWithholdingExemptionTooltip,
} from '../../../shared/taxWithholdingExemption';

const btnBase = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  width: '24px',
  height: '24px',
  borderRadius: '6px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '13px',
  lineHeight: 1,
};

const mutedColor = 'var(--bk-c-98a2b3, #98A2B3)';

function activeStyle(bg) {
  return { ...btnBase, background: bg, color: 'var(--bk-c-ffffff, #ffffff)' };
}
function inactiveStyle() {
  return { ...btnBase, color: mutedColor };
}

const SCISSORS_ACTIVE_BACKGROUND = 'var(--bk-c-1982c4, #1982C4)';
const EYE_ACTIVE_BACKGROUND = 'var(--bk-c-e8833a, #E8833A)';

// The tax marker colours the glyph rather than filling the button, matching the
// category tag icon on the same row — two filled chips side by side read as one
// control group and drown out the row's own numbers.
const TAX_ACTIVE_COLOR = 'var(--bk-c-1982c4, #1982C4)';
function tintedStyle(color) {
  return { ...btnBase, color };
}


const InlineRowToggles = ({ budgetLineItem, offer, showVisibilityToggle, sectionLineItems, onToggle }) => {
  const scissorsApplicable =
    budgetLineItem.category === 'EXPENSES' && offer?.revenue_base === 'adjusted_gross';
  const visibilityApplicable = showVisibilityToggle && !budgetLineItem.isReservedAncillaryIncome;
  // Marking is what tells the engine which row to re-base, and — since O-4778 —
  // what switches the pass-through on at all. Only one row per budget can be the
  // tax, so once one is marked the control leaves every other row; the marked
  // row keeps it, because that is how the marking is undone. (O-4509, O-4782)
  const taxApplicable = canMarkAsAdmissionTax(budgetLineItem, sectionLineItems);

  const exemptionApplicable = canToggleTaxWithholdingExemption(budgetLineItem, offer);

  if (!scissorsApplicable && !visibilityApplicable && !taxApplicable && !exemptionApplicable) return null;

  const deducted = !!budgetLineItem.deducted_before_split;
  const exempt = !!budgetLineItem.exempt_from_tax_withholding;
  const visible = budgetLineItem.show_in_offer ?? true;
  const isTax = budgetLineItem.kind === KIND_ADMISSION_TAX;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
      {scissorsApplicable && (
        <button
          type="button"
          aria-label={deducted ? "Don't deduct before split" : 'Deduct before split'}
          title={deducted ? 'Deducted before split' : 'Deduct before split'}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle('deducted_before_split', !deducted); }}
          style={deducted ? activeStyle(SCISSORS_ACTIVE_BACKGROUND) : inactiveStyle()}
        >
          <i className="fas fa-cut" aria-hidden="true" />
        </button>
      )}
      {exemptionApplicable && (
        <Tippy
          content={taxWithholdingExemptionTooltip(exempt)}
          placement="top"
          theme="material"
          animation="shift-away"
        >
          <button
            type="button"
            data-testid={TAX_WITHHOLDING_EXEMPTION_TEST_ID}
            aria-label={TAX_WITHHOLDING_EXEMPTION_LABEL}
            aria-pressed={exempt}
            onClick={(event) => { event.preventDefault(); event.stopPropagation(); onToggle('exempt_from_tax_withholding', !exempt); }}
            style={exempt ? activeStyle(TAX_WITHHOLDING_EXEMPT_COLOR) : tintedStyle('var(--bk-c-d5d7da, #D5D7DA)')}
          >
            <i className={TAX_WITHHOLDING_EXEMPTION_ICON} aria-hidden="true" />
          </button>
        </Tippy>
      )}
      {visibilityApplicable && (
        <button
          type="button"
          aria-label={visible ? "Don't show in offer" : 'Show in offer'}
          title={visible ? "Don't show in offer" : 'Show in offer'}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle('show_in_offer', !visible); }}
          style={!visible ? activeStyle(EYE_ACTIVE_BACKGROUND) : inactiveStyle()}
        >
          <i className={`fas ${visible ? 'fa-eye' : 'fa-eye-slash'}`} aria-hidden="true" />
        </button>
      )}
      {taxApplicable && (
        <Tippy
          content={isTax
            ? 'This line is the admission tax: it is re-based to the net gate and added on top instead of deducted. Click to unmark, which also turns "Tax after facility fee" off.'
            : 'Mark this line as the admission tax. This also turns "Tax after facility fee" on, re-basing it to the net gate and adding it on top instead of deducting it from the deal.'}
          placement="top"
          theme="material"
          animation="shift-away"
        >
          <button
            type="button"
            data-testid="mark-as-admission-tax"
            aria-label={isTax ? 'Not the admission tax' : 'Mark as the admission tax'}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle('kind', isTax ? null : KIND_ADMISSION_TAX); }}
            style={isTax ? tintedStyle(TAX_ACTIVE_COLOR) : inactiveStyle()}
          >
            <i className="fas fa-percentage" aria-hidden="true" />
          </button>
        </Tippy>
      )}
    </span>
  );
};

InlineRowToggles.propTypes = {
  budgetLineItem: PropTypes.object.isRequired,
  offer: PropTypes.object,
  showVisibilityToggle: PropTypes.bool,
  // Omitting this silently re-admits the multi-marker bug: canMarkAsAdmissionTax
  // returns true for every eligible row when it has nothing to compare against.
  sectionLineItems: PropTypes.array,
  onToggle: PropTypes.func.isRequired,
};

export default InlineRowToggles;
